import { Controller } from "@hotwired/stimulus";

// One per <section> in the filter drawer. Toggles open/closed via
// aria-expanded + .is-open class; counts active FILTER TYPES (not values)
// in the body and shows count badge + .has-active class.
export default class extends Controller {
  static targets = ["count"];

  connect() {
    // Coalesce mutation bursts and input/change events into one per-frame
    // recount. Select2's dropdown renders inside the offcanvas, so every
    // option-class toggle (~250 per keystroke) mutates this section's
    // subtree if the section contains the Select2; without coalescing,
    // _recount runs hundreds of times per keystroke and walks every named
    // input in the section body each time.
    this._pendingFrame = null;
    this._scheduleRecount = () => {
      if (this._pendingFrame != null) return;
      this._pendingFrame = requestAnimationFrame(() => {
        this._pendingFrame = null;
        this._recount();
      });
    };
    this.element.addEventListener("input", this._scheduleRecount);
    this.element.addEventListener("change", this._scheduleRecount);
    // Pills toggle .is-active via JS class manipulation, not native events.
    // MutationObserver catches that.
    this._observer = new MutationObserver(this._scheduleRecount);
    this._observer.observe(this.element, {
      subtree: true, attributes: true,
      attributeFilter: ["class", "aria-pressed"]
    });
    this._recount();
  }

  disconnect() {
    this.element.removeEventListener("input", this._scheduleRecount);
    this.element.removeEventListener("change", this._scheduleRecount);
    this._observer?.disconnect();
    if (this._pendingFrame != null) {
      cancelAnimationFrame(this._pendingFrame);
      this._pendingFrame = null;
    }
  }

  toggle(event) {
    const open = this.element.classList.toggle("is-open");
    event.currentTarget.setAttribute("aria-expanded", open ? "true" : "false");
  }

  _recount() {
    const body = this.element.querySelector(".filter-section__body");
    if (!body) return;
    let n = 0;

    // Each <input name=...> with non-blank value counts as one filter type.
    const seen = new Set();
    body.querySelectorAll("[name]").forEach((el) => {
      const name = el.getAttribute("name");
      if (!name || seen.has(name)) return;

      if (el.type === "checkbox" || el.type === "radio") {
        if (el.checked && el.value !== "") { seen.add(name); n += 1; }
      } else if (el.tagName === "SELECT" && el.multiple) {
        if (el.selectedOptions.length > 0) { seen.add(name); n += 1; }
      } else if (el.value && el.value.trim() !== "") {
        seen.add(name); n += 1;
      }
    });

    // chip-input tokens (city) — count as 1 filter type per chip-input
    body.querySelectorAll(".filter-chip-input").forEach((ci) => {
      if (ci.querySelector(".filter-chip-input__token")) n += 1;
    });

    if (this.hasCountTarget) this.countTarget.textContent = String(n);
    this.element.classList.toggle("has-active", n > 0);
  }
}
